import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useServiceWorker } from '../context/ServiceWorkerContext';
import { useToast } from '../context/ToastContext';
import { usePlants } from '../hooks/usePlants';
import { BulkUpgradeSection } from '../components/settings/BulkUpgradeSection';
import { CitySetupSheet } from '../components/settings/CitySetupSheet';
import { changelog } from '../data/changelog';

const APP_VERSION = __APP_VERSION__;

export default function Settings() {
  const { userDoc, signOut } = useAuth();
  const { showSuccess } = useToast();
  const { plantCount } = usePlants();
  const [citySheetOpen, setCitySheetOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showOlderChanges, setShowOlderChanges] = useState(false);

  // Registration lives at the app root so a waiting update is noticed from any
  // screen, not just this one.
  const { needRefresh, applyUpdate, checkForUpdate } = useServiceWorker();

  const handleCheckForUpdates = useCallback(async () => {
    setRefreshing(true);

    if (needRefresh) {
      await applyUpdate(); // reloads the page
      return;
    }

    if (await checkForUpdate()) {
      await applyUpdate(); // reloads the page
      return;
    }

    showSuccess('Already on the latest version');
    setRefreshing(false);
  }, [needRefresh, applyUpdate, checkForUpdate, showSuccess]);

  return (
    <main className="min-h-screen bg-background">
      <div className="px-6 py-6">
        {/* Account Section */}
        <section className="mb-8">
          <h2 className="section-header mb-4">Account</h2>
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
            <div>
              <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">Display Name</p>
              <p className="font-display font-bold text-editorial-black">{userDoc?.displayName || '—'}</p>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">Email</p>
              <p className="font-serif text-editorial-black">{userDoc?.email || '—'}</p>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">Account Tier</p>
              <p className="font-serif text-editorial-black capitalize">Beta</p>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">City</p>
                  <p className="font-serif text-editorial-black">{userDoc?.city || 'Not set'}</p>
                  {userDoc?.climateZone && (
                    <p className="text-xs font-sans text-gray-400 mt-0.5 capitalize">{userDoc.climateZone} climate</p>
                  )}
                </div>
                <button
                  onClick={() => setCitySheetOpen(true)}
                  className="text-xs font-sans uppercase tracking-[0.15em] text-primary py-2 px-1"
                  aria-label={userDoc?.city ? 'Change city' : 'Set city'}
                >
                  {userDoc?.city ? 'Change' : 'Set'}
                </button>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">Plant Count</p>
              <p className="font-serif text-editorial-black">{plantCount}</p>
            </div>
          </div>
        </section>

        {/* Bulk Upgrade Section */}
        <BulkUpgradeSection />

        {/* Sign Out */}
        <section className="mb-8">
          <button
            onClick={signOut}
            className="w-full rounded-lg border border-gray-200 bg-white p-4 font-sans font-medium text-danger hover:bg-danger/5 transition-colors text-center"
            aria-label="Sign out"
          >
            Sign Out
          </button>
        </section>

        {/* App Info Section */}
        <section>
          <h2 className="section-header mb-4">App Info</h2>
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">Version</p>
                <p className="font-serif text-editorial-black">{APP_VERSION}</p>
              </div>
              <button
                onClick={handleCheckForUpdates}
                disabled={refreshing}
                className="text-xs font-sans uppercase tracking-[0.15em] text-primary py-2 px-1 disabled:opacity-50"
                aria-label={needRefresh ? 'Apply update' : 'Check for updates'}
              >
                {refreshing ? 'Updating...' : needRefresh ? 'Update Now' : 'Check for Updates'}
              </button>
            </div>
            {/* What's New */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-sans text-gray-400 uppercase tracking-wider mb-2">What's New</p>
              {(showOlderChanges ? changelog : changelog.slice(0, 1)).map((entry) => (
                <div key={entry.version} className="mb-3 last:mb-0">
                  <p className="font-sans font-bold text-sm text-editorial-black">
                    v{entry.version}
                    <span className="font-normal text-gray-400 ml-2 text-xs">{entry.date}</span>
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {entry.changes.map((change, i) => (
                      <li key={i} className="text-sm font-serif text-gray-600 flex gap-2">
                        <span className="text-primary/50 shrink-0">·</span>
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {changelog.length > 1 && (
                <button
                  onClick={() => setShowOlderChanges((v) => !v)}
                  className="text-xs font-sans text-primary uppercase tracking-[0.15em] mt-1"
                  aria-label={showOlderChanges ? 'Show fewer updates' : 'Show older updates'}
                >
                  {showOlderChanges ? 'Show less' : 'Show older'}
                </button>
              )}
            </div>
            <div className="border-t border-gray-200 pt-4">
              <a
                href="https://chloropal.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between font-serif text-editorial-black hover:text-primary transition-colors"
              >
                <span>Privacy Policy</span>
                <span className="material-symbols-outlined text-gray-400" style={{ fontSize: 18 }} aria-hidden="true">
                  open_in_new
                </span>
              </a>
            </div>
          </div>
        </section>
      </div>

      <CitySetupSheet open={citySheetOpen} onOpenChange={setCitySheetOpen} />
    </main>
  );
}
